// useColoringProjects.js
// Hook React pour le Carnet de couleurs.
// Expose l'etat des projets actifs + les actions CRUD via le service.

import { useState, useEffect, useCallback } from 'react';
import {
  listActiveProjects,
  listAllProjects,
  getProject,
  getActiveProjectQuota,
} from '../services/coloringProjectsService';

/**
 * Hook principal — liste des projets actifs + actions globales.
 * @param {{ isContributor?: boolean, limit?: number }} options
 */
export function useActiveProjects({ isContributor = false, limit = 3 } = {}) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const quota = getActiveProjectQuota(isContributor);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listActiveProjects(limit);
      setProjects(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { projects, loading, error, quota, reload };
}

/**
 * Hook — tous les projets (actifs + termines).
 */
export function useAllProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAllProjects();
      setProjects(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { projects, loading, error, reload };
}

/**
 * Hook — detail d'un projet + ses marqueurs.
 * @param {string|null} projectId
 */
export function useProjectDetail(projectId) {
  const [project, setProject] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getProject(projectId);
      if (data) {
        setProject(data.project);
        setMarkers(data.markers);
      } else {
        setError(new Error('Projet introuvable'));
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { project, markers, loading, error, reload, setMarkers };
}
